import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Archive, Combine, Plus, Users } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  AudienceSummary,
  CampaignSummary,
} from "@shared-core/data/marketing-types";
import {
  formatDateOrDash,
  formatShortDateOrDash,
} from "@shared-core/domain/formatters";
import { paths } from "@shared-core/routing/routes";
import type { CampaignGoal } from "@shared-core/data/api-types";
import {
  CAMPAIGN_GOALS,
  CAMPAIGN_GOAL_LABELS,
  NO_TIERS_YET_NOTE,
} from "@shared-core/domain/direct-mail-campaigns";
import { ApiError } from "@shared-core/data/api/errors";
import { useIsAdmin } from "../app/auth/hooks";
import { MarketingNav } from "../app/components/domain/marketing/MarketingNav";
import {
  campaignDisplayStatus,
  campaignStatusLabel,
  campaignStatusVariant,
} from "../app/components/domain/marketing/marketingDisplay";
import { Badge } from "../app/components/primitives/badge";
import { Button } from "../app/components/primitives/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../app/components/primitives/card";
import { Checkbox } from "../app/components/primitives/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../app/components/primitives/dialog";
import { Input } from "../app/components/primitives/input";
import { Label } from "../app/components/primitives/label";
import {
  RadioGroup,
  RadioGroupItem,
} from "../app/components/primitives/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../app/components/primitives/select";

function fmtWindow(start: string, end: string): string {
  if (!start || !end) return "No window set";
  return `${formatShortDateOrDash(start)} – ${formatShortDateOrDash(end)}`;
}

/** The actual Mailchimp send date once the campaign has sent; falls back to
 * the CIS activation window for drafts/unsent campaigns, which have no
 * Mailchimp send time yet. */
function fmtSendDate(c: CampaignSummary): string {
  if (c.mailchimp_send_time) {
    return `Sent ${formatDateOrDash(c.mailchimp_send_time)}`;
  }
  return fmtWindow(c.window_start, c.window_end);
}

export function CampaignsPage() {
  const dataAdapter = useDataAdapter();
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const [campaigns, setCampaigns] = useState<CampaignSummary[]>([]);
  const [audiences, setAudiences] = useState<AudienceSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  // Form state
  const [newName, setNewName] = useState("");
  const [newGoal, setNewGoal] = useState<CampaignGoal | "">("");
  const [newDescription, setNewDescription] = useState("");
  const [newAudienceId, setNewAudienceId] = useState("");
  // Tranche count is a plain text input backing an integer — kept as a
  // string so the field can be blank/cleared while typing (defaults to 1,
  // a plain single-send campaign, when empty or invalid).
  const [newTrancheCountInput, setNewTrancheCountInput] = useState("1");
  const [newRemindersEnabled, setNewRemindersEnabled] = useState(false);
  // What the campaign starts with: email tranches, or one draft mailing
  // (direct-mail campaigns). A mailing start has no tranches.
  const [newStartsWith, setNewStartsWith] = useState<"email" | "mailing">(
    "email",
  );
  const [newMailingName, setNewMailingName] = useState("Mailing 1");
  const [newMailingDate, setNewMailingDate] = useState("");
  const [createError, setCreateError] = useState<string | null>(null);

  // Legacy-send-import dialog state — admin-only, deliberately separate
  // from "New campaign" given how different their risk profiles are (this
  // one never sends anything, but writes directly into commercial-outcome
  // reporting).
  const [legacyOpen, setLegacyOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [legacyTargetCampaignId, setLegacyTargetCampaignId] = useState("");
  const [legacyMailchimpId, setLegacyMailchimpId] = useState("");

  // Consolidate-into-project dialog state — admin-only, one-off backfill
  // for campaigns created before "Campaign" meant the project. See
  // docs/features/campaign-projects/08_existing_campaign_consolidation_plan.md.
  const [consolidateOpen, setConsolidateOpen] = useState(false);
  const [consolidating, setConsolidating] = useState(false);
  const [consolidateError, setConsolidateError] = useState<string | null>(null);
  const [consolidateName, setConsolidateName] = useState("");
  const [consolidateAudienceId, setConsolidateAudienceId] = useState("");
  const [consolidateCampaignIds, setConsolidateCampaignIds] = useState<
    string[]
  >([]);
  // Candidates for this dialog include archived campaigns alongside the
  // page's normal (non-archived) list — a legacy campaign due for
  // consolidation may well have already been archived (e.g. by hand, or by
  // a prior failed consolidation attempt), and archiving doesn't change
  // whether it's a valid merge input.
  const [consolidateCandidates, setConsolidateCandidates] = useState<
    CampaignSummary[]
  >([]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(false);
      try {
        const [campaignsRes, audiencesRes] = await Promise.all([
          dataAdapter.listMarketingCampaigns({ pageSize: 100 }),
          dataAdapter.listMarketingAudiences({ pageSize: 100 }),
        ]);
        if (!cancelled) {
          setCampaigns(campaignsRes.items);
          setAudiences(audiencesRes.items);
        }
      } catch (e) {
        console.error("marketing.campaigns.list.failed", e);
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [dataAdapter]);

  function openNewDialog() {
    setNewName("");
    setNewGoal("");
    setNewDescription("");
    setNewAudienceId(audiences[0]?.id ?? "");
    setNewTrancheCountInput("1");
    setNewRemindersEnabled(false);
    setNewStartsWith("email");
    setNewMailingName("Mailing 1");
    setNewMailingDate("");
    setCreateError(null);
    setNewOpen(true);
  }

  const newTrancheCount = Math.max(1, parseInt(newTrancheCountInput, 10) || 1);
  const newSendCount =
    newTrancheCount + (newRemindersEnabled ? newTrancheCount : 0);

  const startsWithMailing = newStartsWith === "mailing";
  const canCreate =
    Boolean(newName.trim()) &&
    Boolean(newAudienceId) &&
    newGoal !== "" &&
    (!startsWithMailing ||
      (Boolean(newMailingName.trim()) && Boolean(newMailingDate)));

  async function handleCreate() {
    if (!canCreate) return;
    setCreating(true);
    setCreateError(null);
    try {
      const campaign = await dataAdapter.createMarketingCampaign({
        name: newName.trim(),
        audience_id: newAudienceId,
        goal: newGoal as CampaignGoal,
        description: newDescription.trim() || null,
        ...(startsWithMailing
          ? {
              starts_with: "mailing",
              mailing_name: newMailingName.trim(),
              mailing_date: newMailingDate,
            }
          : {
              tranche_count: newTrancheCount,
              reminders_enabled: newRemindersEnabled,
            }),
      });
      setCampaigns((prev) => [campaign, ...prev]);
      setNewOpen(false);
      navigate(paths.marketingCampaignWorkspace({ campaignId: campaign.id }));
    } catch (e) {
      console.error("marketing.campaigns.create.failed", {
        status: e instanceof ApiError ? e.status : undefined,
      });
      setCreateError(
        e instanceof ApiError && e.status === 400 && e.detail
          ? e.detail
          : "Could not create the campaign. Please try again.",
      );
    } finally {
      setCreating(false);
    }
  }

  function openLegacyDialog() {
    setLegacyTargetCampaignId(campaigns[0]?.id ?? "");
    setLegacyMailchimpId("");
    setImportError(null);
    setLegacyOpen(true);
  }

  async function handleImportLegacy() {
    if (!legacyTargetCampaignId || !legacyMailchimpId.trim()) return;
    setImporting(true);
    setImportError(null);
    try {
      await dataAdapter.importLegacySend({
        target_campaign_id: legacyTargetCampaignId,
        mailchimp_campaign_id: legacyMailchimpId.trim(),
      });
      setLegacyOpen(false);
      navigate(
        paths.marketingCampaignWorkspace({
          campaignId: legacyTargetCampaignId,
        }),
      );
    } catch (e) {
      console.error("marketing.campaigns.import_legacy_send.failed", e);
      setImportError(
        "Could not import this send. Check the Mailchimp campaign ID and try again.",
      );
    } finally {
      setImporting(false);
    }
  }

  async function openConsolidateDialog() {
    setConsolidateName("");
    setConsolidateAudienceId(audiences[0]?.id ?? "");
    setConsolidateCampaignIds([]);
    setConsolidateError(null);
    setConsolidateCandidates(campaigns);
    setConsolidateOpen(true);
    try {
      const archivedRes = await dataAdapter.listMarketingCampaigns({
        pageSize: 100,
        status: "archived",
      });
      const byId = new Map(campaigns.map((c) => [c.id, c]));
      for (const c of archivedRes.items) byId.set(c.id, c);
      setConsolidateCandidates([...byId.values()]);
    } catch (e) {
      console.error("marketing.campaigns.list_archived.failed", e);
    }
  }

  function toggleConsolidateCampaign(campaignId: string) {
    setConsolidateCampaignIds((prev) =>
      prev.includes(campaignId)
        ? prev.filter((id) => id !== campaignId)
        : [...prev, campaignId],
    );
  }

  async function handleConsolidate() {
    if (
      !consolidateName.trim() ||
      !consolidateAudienceId ||
      consolidateCampaignIds.length < 2
    )
      return;
    setConsolidating(true);
    setConsolidateError(null);
    try {
      const project = await dataAdapter.consolidateMarketingCampaigns({
        campaign_ids: consolidateCampaignIds,
        project_name: consolidateName.trim(),
        audience_id: consolidateAudienceId,
      });
      setConsolidateOpen(false);
      navigate(paths.marketingCampaignWorkspace({ campaignId: project.id }));
    } catch (e) {
      console.error("marketing.campaigns.consolidate.failed", e);
      setConsolidateError(
        "Could not consolidate these campaigns. Each one needs exactly one send, and at least two must be selected.",
      );
    } finally {
      setConsolidating(false);
    }
  }

  return (
    <div className="space-y-6">
      <MarketingNav active="campaigns" />

      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl mb-1">Campaigns</h1>
          <p className="text-sm text-muted-foreground">
            Multi-channel efforts, each targeting one audience as an ordered
            cadence.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <>
              <Button variant="outline" onClick={openConsolidateDialog}>
                <Combine className="size-4" />
                Consolidate into project
              </Button>
              <Button variant="outline" onClick={openLegacyDialog}>
                <Archive className="size-4" />
                Import legacy send
              </Button>
            </>
          )}
          <Button onClick={openNewDialog}>
            <Plus className="size-4" />
            New campaign
          </Button>
        </div>
      </div>

      {loading && (
        <div className="text-sm text-muted-foreground">Loading campaigns…</div>
      )}

      {error && !loading && (
        <div className="text-sm p-8 text-center border border-border rounded-lg text-red-600">
          Couldn't load campaigns. Please try again.
        </div>
      )}

      {!loading && !error && campaigns.length === 0 && (
        <div className="text-sm p-8 text-center border border-border rounded-lg space-y-1">
          <p className="font-medium text-foreground">No campaigns yet</p>
          <p className="text-muted-foreground">
            Create a campaign to start an outbound cadence against an audience.
          </p>
        </div>
      )}

      {!loading && !error && campaigns.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2">
          {campaigns.map((c) => (
            <CampaignCard
              key={c.id}
              campaign={c}
              onOpen={() =>
                navigate(paths.marketingCampaignWorkspace({ campaignId: c.id }))
              }
            />
          ))}
        </div>
      )}

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New campaign</DialogTitle>
            <DialogDescription>
              Name the effort, choose its goal and audience, and say what it
              starts with. Email can be split into tranches, each with its own
              send.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="campaign-name">Campaign name</Label>
              <Input
                id="campaign-name"
                placeholder="e.g. Autumn Brochure Drive"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="campaign-goal">Goal</Label>
              <Select
                value={newGoal}
                onValueChange={(v) => setNewGoal(v as CampaignGoal)}
              >
                <SelectTrigger id="campaign-goal">
                  <SelectValue placeholder="Choose a goal…" />
                </SelectTrigger>
                <SelectContent>
                  {CAMPAIGN_GOALS.map((goal) => (
                    <SelectItem key={goal} value={goal}>
                      {CAMPAIGN_GOAL_LABELS[goal]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="campaign-description">
                Description (optional)
              </Label>
              <Input
                id="campaign-description"
                placeholder="What this campaign is for"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Audience</Label>
              <Select value={newAudienceId} onValueChange={setNewAudienceId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose an audience…" />
                </SelectTrigger>
                <SelectContent>
                  {audiences.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Starts with</Label>
              <RadioGroup
                value={newStartsWith}
                onValueChange={(v) =>
                  setNewStartsWith(v as "email" | "mailing")
                }
                className="flex gap-6"
              >
                {(["email", "mailing"] as const).map((option) => (
                  <div key={option} className="flex items-center gap-2">
                    <RadioGroupItem
                      value={option}
                      id={`campaign-starts-with-${option}`}
                    />
                    <Label
                      htmlFor={`campaign-starts-with-${option}`}
                      className="font-normal"
                    >
                      {option === "email" ? "Email" : "Mailing"}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
            {startsWithMailing ? (
              <>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="campaign-mailing-name">Mailing name</Label>
                    <Input
                      id="campaign-mailing-name"
                      value={newMailingName}
                      maxLength={120}
                      onChange={(e) => setNewMailingName(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="campaign-mailing-date">Planned date</Label>
                    <Input
                      id="campaign-mailing-date"
                      type="date"
                      value={newMailingDate}
                      onChange={(e) => setNewMailingDate(e.target.value)}
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Goes to tiers</Label>
                  {/* A brand-new campaign never has tiers yet. */}
                  <p className="text-sm text-muted-foreground">
                    {NO_TIERS_YET_NOTE}
                  </p>
                </div>
              </>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="campaign-tranche-count">Tranches</Label>
                  <Input
                    id="campaign-tranche-count"
                    type="number"
                    min={1}
                    step={1}
                    value={newTrancheCountInput}
                    onChange={(e) => setNewTrancheCountInput(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Splits the audience evenly; each tranche is its own send.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <label className="flex items-center gap-2 text-sm cursor-pointer pt-7">
                    <Checkbox
                      checked={newRemindersEnabled}
                      onCheckedChange={(checked) =>
                        setNewRemindersEnabled(checked === true)
                      }
                    />
                    Send a reminder for each
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Sent 1 week after its tranche, by default.
                  </p>
                </div>
              </div>
            )}
            {!startsWithMailing &&
              (newTrancheCount > 1 || newRemindersEnabled) && (
                <p className="text-xs text-muted-foreground">
                  This will create {newSendCount} send
                  {newSendCount === 1 ? "" : "s"}: {newTrancheCount} tranche
                  {newTrancheCount === 1 ? "" : "s"}
                  {newRemindersEnabled &&
                    ` + ${newTrancheCount} reminder${newTrancheCount === 1 ? "" : "s"}`}
                  .
                </p>
              )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={creating || !canCreate}>
              {creating ? "Creating…" : "Create draft"}
            </Button>
          </DialogFooter>
          {createError && (
            <p className="text-sm text-red-700 dark:text-red-300" role="alert">
              {createError}
            </p>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={legacyOpen} onOpenChange={setLegacyOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import legacy send</DialogTitle>
            <DialogDescription>
              For a send that already happened entirely outside One. Importing
              will pull the send&apos;s engagement data from Mailchimp and
              attach the send to an existing project. Importing never sends
              anything or raises tickets, and the send can&apos;t be activated
              afterwards.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Target project</Label>
              <Select
                value={legacyTargetCampaignId}
                onValueChange={setLegacyTargetCampaignId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a project…" />
                </SelectTrigger>
                <SelectContent>
                  {campaigns.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                The project this send joins as a new tranche.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="legacy-mailchimp-id">Mailchimp campaign ID</Label>
              <Input
                id="legacy-mailchimp-id"
                placeholder="e.g. b1f8a9c2d3"
                value={legacyMailchimpId}
                onChange={(e) => setLegacyMailchimpId(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                The real send date and engagement numbers are pulled straight
                from this campaign in Mailchimp.
              </p>
            </div>
            {importError && (
              <p className="text-xs text-red-600">{importError}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLegacyOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleImportLegacy}
              disabled={
                importing ||
                !legacyTargetCampaignId ||
                !legacyMailchimpId.trim()
              }
            >
              {importing ? "Importing…" : "Import"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={consolidateOpen} onOpenChange={setConsolidateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Consolidate into project</DialogTitle>
            <DialogDescription>
              One-off backfill for campaigns created before "Campaign" meant the
              project. Pick every existing campaign that belongs to one real
              push (e.g. "Suppliers tranche 1" and "Suppliers tranche 1
              REMINDER") and fold them into one multi-tranche project. Reminder
              pairing is worked out from naming automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="consolidate-project-name">Project name</Label>
              <Input
                id="consolidate-project-name"
                placeholder="e.g. Suppliers"
                value={consolidateName}
                onChange={(e) => setConsolidateName(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Audience</Label>
              <Select
                value={consolidateAudienceId}
                onValueChange={setConsolidateAudienceId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose an audience…" />
                </SelectTrigger>
                <SelectContent>
                  {audiences.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Each merged send already has its own real history — this is just
                the project's own display audience.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label>Campaigns to merge (pick at least two)</Label>
              <div className="max-h-64 overflow-y-auto border border-border rounded-md divide-y divide-border">
                {consolidateCandidates.map((c) => (
                  <label
                    key={c.id}
                    className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer"
                  >
                    <Checkbox
                      checked={consolidateCampaignIds.includes(c.id)}
                      onCheckedChange={() => toggleConsolidateCampaign(c.id)}
                    />
                    {c.name}
                    {c.status === "archived" && (
                      <Badge variant="outline" className="ml-auto">
                        Archived
                      </Badge>
                    )}
                  </label>
                ))}
              </div>
            </div>
            {consolidateError && (
              <p className="text-xs text-red-600">{consolidateError}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConsolidateOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleConsolidate}
              disabled={
                consolidating ||
                !consolidateName.trim() ||
                !consolidateAudienceId ||
                consolidateCampaignIds.length < 2
              }
            >
              {consolidating ? "Consolidating…" : "Consolidate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CampaignCard({
  campaign: c,
  onOpen,
}: {
  campaign: CampaignSummary;
  onOpen: () => void;
}) {
  // Per the campaign-projects glossary: a single-send campaign reads exactly
  // like a plain campaign always has — no "N sends" language until there
  // are genuinely 2+ sends. Uses the real step_count (from a live COUNT in
  // postgres_ro, not tranche_count + reminders_enabled): a legacy project
  // folded together via consolidate/import doesn't follow the normal
  // N-tranches-plus-N-reminders shape those two fields imply, so deriving
  // the count from them undercounts a project like that.
  const sendCount = c.step_count;
  const displayStatus = campaignDisplayStatus(c);
  return (
    <Card
      className="hover:bg-surface-hover transition-colors cursor-pointer"
      onClick={onOpen}
    >
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base">{c.name}</CardTitle>
          <Badge variant={campaignStatusVariant(displayStatus)}>
            {campaignStatusLabel(displayStatus)}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2 text-sm">
          <Users className="size-4 text-muted-foreground" />
          <span className="text-foreground">{c.audience_name}</span>
          {(c.member_count ?? 0) > 0 && (
            <span className="text-muted-foreground">
              · {c.member_count} people · {c.suppressed_count} suppressed
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground">
          <span>
            {c.status === "legacy"
              ? "No cadence"
              : sendCount > 1
                ? `${sendCount} sends`
                : `${c.step_count} cadence steps`}
          </span>
          <span>{c.owner}</span>
          <span>{fmtSendDate(c)}</span>
        </div>
      </CardContent>
    </Card>
  );
}
